import React, { type ReactElement, type ReactNode } from 'react';
import i18n from '@/i18n/config';
import { styles } from './ErrorBoundary.styles';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * Error boundary component to catch React errors.
 * Provides a user-friendly error UI with recovery options.
 */
export class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error) {
    console.error('Error caught by boundary:', error);
  }

  handleTryAgain = () => {
    this.setState({ hasError: false, error: null });
  };

  render(): ReactElement {
    if (this.state.hasError) {
      return (
        <div style={styles.container}>
          <div style={styles.icon}>⚠️</div>
          <h1 style={styles.heading}>{i18n.t('common:errors.somethingWentWrong')}</h1>
          <p style={styles.message}>{i18n.t('common:errors.unexpected')}</p>
          <div style={styles.buttonRow}>
            <button style={styles.primaryButton} onClick={this.handleTryAgain}>
              {i18n.t('common:actions.tryAgain')}
            </button>
            <button style={styles.secondaryButton} onClick={() => globalThis.location.reload()}>
              {i18n.t('common:errors.reloadPage')}
            </button>
          </div>
        </div>
      );
    }

    return <>{this.props.children}</>;
  }
}
